/**
 * Implement Gatsby's Browser APIs in this file.
 *
 * See: https://www.gatsbyjs.org/docs/browser-apis/
 */
// You can delete this file if you're not using it
import React from "react";

import { isCmpAvailable } from "../design-system/shared/cmpAvailability";
import AppDataContextProvider from "../design-system/shared/contexts/app-data/AppDataContextProvider";
import { MediaContextProvider } from "../design-system/shared/contexts/media/MediaContext";
import UiStateContextProvider from "../design-system/shared/contexts/ui-state/UiStateContextProvider";

require("css.escape");

const CONSENT_COOKIE = "c_c";
const OP_CONSENT_COOKIE = "op_chorg_consent";
const oneYearInSec = 60 * 60 * 24 * 365;
const secure = window.location.protocol === "https:" ? "; Secure" : "";
// How long to wait for the async CMP script before giving up.
const CMP_READY_TIMEOUT_MS = 15000;

// Runs once per client session
const bootstrapVendorConsent = () => {
  const getCookie = (name) => {
    const prefix = `${name}=`;
    const match = document.cookie.split("; ").find((c) => c.indexOf(prefix) === 0);
    return match ? match.substring(prefix.length) : undefined;
  };

  // Already consented on a previous visit — nothing to do.
  if (getCookie(CONSENT_COOKIE) === "true" || getCookie(OP_CONSENT_COOKIE) === "true") {
    return;
  }

  // Scope to the Gatsby root so CMP banner clicks dont trigger
  const gatsbyRoot = document.getElementById("___gatsby");
  if (!gatsbyRoot) {
    console.warn("consent bootstrap skipped: #___gatsby not found");
    return;
  }

  const READY_DEADLINE = Date.now() + CMP_READY_TIMEOUT_MS;

  const handleFirstClick = (event) => {
    //recheck if the user has already set consent via the OP_CONSENT_COOKIE
    if (getCookie(OP_CONSENT_COOKIE) === "true" || getCookie(CONSENT_COOKIE) === "true") {
      gatsbyRoot.removeEventListener("click", handleFirstClick, true);
      return;
    }

    // Clicking the cookie preference link opens CMP preferences, so skip the soft opt in.
    if (event.target.closest("#cookie-preference-link")) {
      return;
    }

    if (!isCmpAvailable()) {
      // Prevent the event listener from being attached indefinitely if the CMP never loads.
      // If cmp script hasnt loaded (or is feature toggled off) after 15 seconds the next click releases the event listener for session
      if (Date.now() > READY_DEADLINE) {
        gatsbyRoot.removeEventListener("click", handleFirstClick, true);
      }
      return;
    }

    try {
      window.ours_consent.acceptAll();
      window.ours_consent.hide();
    } catch (err) {
      console.error("ours_consent bootstrap failed", err);
      return;
    }

    document.cookie = `${CONSENT_COOKIE}=true; path=/; max-age=${oneYearInSec}; SameSite=Lax${secure}`;

    gatsbyRoot.removeEventListener("click", handleFirstClick, true);
  };

  gatsbyRoot.addEventListener("click", handleFirstClick, true);
};

const captureFirstConsent = () => {
  const deadline = Date.now() + CMP_READY_TIMEOUT_MS;
  const retryDelay = 250;

  const attach = () => {
    if (isCmpAvailable()) {
      // Capture OP first consent so we do not soft opt in automatically
      window.ours_consent.on("firstConsent", function (event) {
        document.cookie = `${CONSENT_COOKIE}=true; path=/; max-age=${oneYearInSec}; SameSite=Lax${secure}`;
      });
      return;
    }

    // CMP loads asynchronously, so poll until it's ready or we hit the deadline.
    if (Date.now() < deadline) {
      window.setTimeout(attach, retryDelay);
    } else {
      console.warn("captureFirstConsent skipped: CMP never became available");
    }
  };

  attach();
};

export const onClientEntry = () => {
  // IntersectionObserver polyfill for gatsby-background-image (Safari, IE)
  if (!(`IntersectionObserver` in window)) {
    import(`intersection-observer`);
  }

  bootstrapVendorConsent();
  captureFirstConsent();
};

export const wrapRootElement = ({ element }) => (
  <UiStateContextProvider>
    <AppDataContextProvider>
      <MediaContextProvider>{element}</MediaContextProvider>
    </AppDataContextProvider>
  </UiStateContextProvider>
);
